/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { makeStyles, styled } from '@mui/styles';
import { useMediaQuery, Theme, Stack, Box, Zoom, darken, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mainMenuWidth, MenuStatus, secondaryMenuWidth } from '../../constants/Viewer';
import { useAppSelector } from '../../hooks';
import { MenuVariant } from '../../constants/Menu';
import { Tooltip } from './Tooltip';
import { Button } from './Button';

const useStyles = makeStyles<Theme, { mobileView: boolean; menuStatus: MenuStatus; menuVariant: MenuVariant }>({
  fab: {
    position: 'fixed',
    left: ({ mobileView, menuStatus, menuVariant }) =>
      `${
        16 +
        (menuStatus !== MenuStatus.Closed && !mobileView
          ? mainMenuWidth[menuVariant] + secondaryMenuWidth[menuVariant]
          : 0)
      }px`,
    bottom: '16px',
  },
});

const FabMenu = styled(Stack)({
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
});

export const Fab: React.FC<{
  id: string;
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  actions?: {
    id: string;
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
  }[];
}> = ({ id, label = '', icon, onClick, actions }) => {
  const { t } = useTranslation();

  const menuVariant = useAppSelector((state) => state.menu.variant);
  const menuStatus = useAppSelector((state) => state.viewer.menuStatus);

  const mobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const classes = useStyles({ mobileView, menuStatus, menuVariant });

  const [open, setOpen] = React.useState(false);

  return (
    <Box zIndex={1000}>
      <Button
        id={id}
        variant="contained"
        className={classes.fab}
        onClick={onClick}
        onMouseEnter={() => {
          if (actions?.length) setOpen(true);
        }}
        onMouseLeave={() => {
          if (actions?.length) setOpen(false);
        }}
        sx={{
          backgroundColor: (theme: Theme) =>
            open ? `${darken(theme.palette.secondary.main, 0.4)} !important` : undefined,
        }}
      >
        <Stack direction="row" spacing="8px">
          <Box display="flex" ml="-5px !important">
            {icon}
          </Box>
          <Typography color="#FFFFFF">{t(label)}</Typography>
        </Stack>
        {!!actions?.length && (
          <FabMenu spacing="8px">
            {actions.map(({ id: actionId, label: actionLabel, icon: actionIcon, onClick: onActionClick }) => (
              <Tooltip key={actionId} title={t(actionLabel)} placement="right" open={open}>
                <Box height="56px" width="56px">
                  <Zoom in={open} unmountOnExit>
                    <Button
                      id={actionId}
                      className={classes.fabButton}
                      variant="contained"
                      sx={{
                        borderRadius: '100%',
                        padding: '8px',
                        minWidth: '56px',
                        height: '56px',
                      }}
                      onClick={() => {
                        setOpen(false);
                        onActionClick();
                      }}
                    >
                      {actionIcon}
                    </Button>
                  </Zoom>
                </Box>
              </Tooltip>
            ))}
          </FabMenu>
        )}
      </Button>
    </Box>
  );
};
